import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";
import { NIVEL_EDUCATIVO_911, FUNCION_PERSONAL_911, labelFuncion, labelNivel } from "@/lib/sepCatalogos";
import { parseCURP, isValidCURP } from "@/lib/curp";

const ESTADOS_CIVIL = ["SOLTERO(A)", "CASADO(A)", "UNIÓN LIBRE", "DIVORCIADO(A)", "VIUDO(A)"];
const DISC_OPTS = ["CEGUERA", "BAJA VISIÓN", "SORDERA", "HIPOACUSIA", "MOTRIZ", "INTELECTUAL", "PSICOSOCIAL", "OTRA"];

const emptyForm = {
  plantel_id: "",
  nombre: "",
  apellido_paterno: "",
  apellido_materno: "",
  sexo: "H",
  funcion: FUNCION_PERSONAL_911[0].value,
  nivel_educativo: NIVEL_EDUCATIVO_911[0].value,
  nivel_educativo_otros: "",
  curp: "",
  rfc: "",
  celular: "",
  correo_personal: "",
  fecha_nacimiento: "",
  estado_civil: "",
  plaza: "",
  fecha_ingreso_sep: "",
  discapacidad: "",
  lenguas_indigenas: "",
  horas_semanales: 0,
};

// Funciones cuya asignación semanal alimenta V.4 (Horas semanales impartidas
// por el personal docente especial). Solo estas 4 muestran el campo Horas.
const FUNCIONES_CON_HORAS = new Set([
  "PROFESOR_EDUCACION_FISICA",
  "PROFESOR_ARTISTICAS",
  "PROFESOR_TECNOLOGICAS",
  "PROFESOR_IDIOMAS",
]);
const showHoras = (funcion) => FUNCIONES_CON_HORAS.has(funcion);

export default function PersonalPorFuncion() {
  const [items, setItems] = useState([]);
  const [planteles, setPlanteles] = useState([]);
  const [filterPlantel, setFilterPlantel] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rfcManual, setRfcManual] = useState(false);

  const load = async () => {
    try {
      const [p, pf] = await Promise.all([
        api.get("/planteles"),
        api.get("/personal-funcion", { params: filterPlantel !== "__all__" ? { plantel_id: filterPlantel } : {} }),
      ]);
      setPlanteles(p.data);
      setItems(pf.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [filterPlantel]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, plantel_id: planteles[0]?.id || "" });
    setRfcManual(false);
    setOpen(true);
  };

  const startEdit = (it) => {
    setEditing(it);
    setForm({
      plantel_id: it.plantel_id,
      nombre: it.nombre || "",
      apellido_paterno: it.apellido_paterno || "",
      apellido_materno: it.apellido_materno || "",
      sexo: it.sexo || "H",
      funcion: it.funcion || FUNCION_PERSONAL_911[0].value,
      nivel_educativo: it.nivel_educativo || NIVEL_EDUCATIVO_911[0].value,
      nivel_educativo_otros: it.nivel_educativo_otros || "",
      curp: it.curp || "",
      rfc: it.rfc || "",
      celular: it.celular || "",
      correo_personal: it.correo_personal || "",
      fecha_nacimiento: it.fecha_nacimiento || "",
      estado_civil: it.estado_civil || "",
      plaza: it.plaza || "",
      fecha_ingreso_sep: it.fecha_ingreso_sep || "",
      discapacidad: it.discapacidad || "",
      lenguas_indigenas: it.lenguas_indigenas || "",
      horas_semanales: it.horas_semanales || 0,
    });
    // Si el RFC guardado no coincide con el prefijo de la CURP, bloquear autofill
    const rfc0 = (it.rfc || "").toUpperCase();
    const curp0 = (it.curp || "").toUpperCase();
    setRfcManual(!!rfc0 && (curp0.length < 10 || rfc0 !== curp0.slice(0, 10)));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.curp && !isValidCURP(form.curp)) {
      toast.error("La CURP debe tener 18 caracteres, con dígitos en las posiciones 5 a 10.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.nivel_educativo !== "OTROS") payload.nivel_educativo_otros = "";
      if (editing) {
        await api.put(`/personal-funcion/${editing.id}`, payload);
        toast.success("Registro actualizado");
      } else {
        await api.post("/personal-funcion", payload);
        toast.success("Personal registrado");
      }
      setOpen(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/personal-funcion/${confirm.id}`);
      toast.success("Registro eliminado");
      setConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const plantelName = useMemo(() => {
    const map = {};
    planteles.forEach((p) => { map[p.id] = p.nombre; });
    return map;
  }, [planteles]);

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Censo 911 · secciones 12–14</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Personal por Función
            </h1>
            <p className="text-sm text-[#545454] mt-1">
              Registre a cada persona por función y nivel educativo. Los conteos se agregan automáticamente en el reporte 911.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {planteles.length > 1 && (
              <div className="min-w-[260px]">
                <Label className="text-xs">Filtrar por plantel</Label>
                <Select value={filterPlantel} onValueChange={setFilterPlantel}>
                  <SelectTrigger data-testid="pf-filter-plantel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="btn-add-personal-funcion" onClick={startCreate} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuevo registro
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>Nombre completo</TableHead>
                <TableHead className="w-16">Sexo</TableHead>
                <TableHead>Función</TableHead>
                <TableHead>Nivel educativo</TableHead>
                {planteles.length > 1 && <TableHead>Plantel</TableHead>}
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={planteles.length > 1 ? 6 : 5} className="text-center py-10">
                    <UserCog className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                    <div className="text-sm text-[#545454]">
                      Sin personal por función registrado
                    </div>
                  </TableCell>
                </TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`pf-row-${it.id}`}>
                  <TableCell className="font-medium">
                    {`${it.nombre} ${it.apellido_paterno} ${it.apellido_materno}`.trim() || "—"}
                  </TableCell>
                  <TableCell className="text-center">{it.sexo}</TableCell>
                  <TableCell>{labelFuncion(it.funcion)}</TableCell>
                  <TableCell>
                    {labelNivel(it.nivel_educativo)}
                    {it.nivel_educativo === "OTROS" && it.nivel_educativo_otros && (
                      <span className="text-xs text-[#6B7280] ml-1">({it.nivel_educativo_otros})</span>
                    )}
                  </TableCell>
                  {planteles.length > 1 && (
                    <TableCell className="text-xs text-[#545454]">{plantelName[it.plantel_id] || "—"}</TableCell>
                  )}
                  <TableCell className="text-right">
                    <Button data-testid={`btn-edit-pf-${it.id}`} variant="ghost" size="sm" onClick={() => startEdit(it)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`btn-delete-pf-${it.id}`} variant="ghost" size="sm" onClick={() => setConfirm(it)}>
                      <Trash2 className="w-4 h-4 text-[#9F2241]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar personal" : "Nuevo personal"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre(s) *</Label>
              <Input data-testid="pf-nombre" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Apellido paterno *</Label>
              <Input data-testid="pf-ap" required value={form.apellido_paterno} onChange={(e) => setForm({ ...form, apellido_paterno: e.target.value.toUpperCase() })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Apellido materno</Label>
              <Input value={form.apellido_materno} onChange={(e) => setForm({ ...form, apellido_materno: e.target.value.toUpperCase() })} />
            </div>
            {planteles.length > 1 && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Plantel *</Label>
                <Select value={form.plantel_id} onValueChange={(v) => setForm({ ...form, plantel_id: v })}>
                  <SelectTrigger data-testid="pf-plantel"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                  <SelectContent>
                    {planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Sexo *</Label>
              <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                <SelectTrigger data-testid="pf-sexo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="H">Hombre</SelectItem>
                  <SelectItem value="M">Mujer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Función *</Label>
              <Select value={form.funcion} onValueChange={(v) => setForm({ ...form, funcion: v, horas_semanales: showHoras(v) ? form.horas_semanales : 0 })}>
                <SelectTrigger data-testid="pf-funcion"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {FUNCION_PERSONAL_911.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {showHoras(form.funcion) && (
              <div className="space-y-1.5">
                <Label className="text-xs">Horas semanales</Label>
                <Input
                  type="number"
                  min={0}
                  data-testid="pf-horas"
                  value={form.horas_semanales || 0}
                  onChange={(e) => setForm({ ...form, horas_semanales: parseInt(e.target.value || "0", 10) || 0 })}
                  placeholder="0"
                />
                <p className="text-[10px] text-[#9CA3AF]">Se sumará automáticamente en V.4 del reporte 911.</p>
              </div>
            )}
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Nivel educativo *</Label>
              <Select value={form.nivel_educativo} onValueChange={(v) => setForm({ ...form, nivel_educativo: v })}>
                <SelectTrigger data-testid="pf-nivel-educativo"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {NIVEL_EDUCATIVO_911.map((n) => (
                    <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.nivel_educativo === "OTROS" && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Especifique *</Label>
                <Input
                  data-testid="pf-nivel-otros"
                  value={form.nivel_educativo_otros}
                  onChange={(e) => setForm({ ...form, nivel_educativo_otros: e.target.value.toUpperCase() })}
                  placeholder="Detalle del nivel educativo"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">CURP</Label>
              <Input
                data-testid="pf-curp"
                value={form.curp}
                maxLength={18}
                className="font-mono"
                onChange={(e) => {
                  const curp = e.target.value.toUpperCase();
                  const parsed = parseCURP(curp);
                  const rfcAutoPrefix = curp.length >= 10 ? curp.slice(0, 10) : "";
                  setForm((f) => ({
                    ...f,
                    curp,
                    fecha_nacimiento: parsed?.fecha_nacimiento || f.fecha_nacimiento,
                    sexo: parsed?.genero || f.sexo,
                    rfc: !rfcManual && rfcAutoPrefix ? rfcAutoPrefix : f.rfc,
                  }));
                }}
              />
              {form.curp && form.curp.length > 0 && !isValidCURP(form.curp) && (
                <p className="text-[11px] text-[#9F2241]" data-testid="pf-curp-error">
                  Formato de CURP inválido. Debe tener 18 caracteres (4 letras + 6 dígitos + H/M + 5 letras + 2 alfanuméricos).
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">RFC</Label>
              <Input
                data-testid="pf-rfc"
                value={form.rfc}
                maxLength={13}
                className="font-mono"
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  setRfcManual(val.length > 0);
                  setForm({ ...form, rfc: val });
                }}
              />
              {!rfcManual && form.curp?.length >= 10 && (
                <p className="text-[10px] text-[#9CA3AF]">Prefijo autocompletado desde la CURP · agregue homoclave (3 caracteres)</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fecha de nacimiento</Label>
              <Input
                type="date"
                data-testid="pf-fecha-nac"
                value={form.fecha_nacimiento || ""}
                onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                readOnly={!!parseCURP(form.curp)?.fecha_nacimiento}
                className={parseCURP(form.curp)?.fecha_nacimiento ? "bg-[#F3F4F6] cursor-not-allowed" : ""}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Estado civil</Label>
              <Select value={form.estado_civil || "__none__"} onValueChange={(v) => setForm({ ...form, estado_civil: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="pf-estado-civil"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin especificar —</SelectItem>
                  {ESTADOS_CIVIL.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Celular personal</Label>
              <Input data-testid="pf-celular" value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })} placeholder="Ej. 8331234567" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Correo personal</Label>
              <Input data-testid="pf-correo-personal" type="email" value={form.correo_personal} onChange={(e) => setForm({ ...form, correo_personal: e.target.value })} placeholder="nombre@dominio.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Plaza / Clave</Label>
              <Input data-testid="pf-plaza" value={form.plaza} onChange={(e) => setForm({ ...form, plaza: e.target.value.toUpperCase() })} placeholder="Ej. E0281" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fecha de ingreso al servicio</Label>
              <Input
                type="date"
                data-testid="pf-fecha-ingreso-sep"
                value={form.fecha_ingreso_sep || ""}
                onChange={(e) => setForm({ ...form, fecha_ingreso_sep: e.target.value })}
              />
            </div>
            <DialogFooter className="md:col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-pf" type="submit" disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el registro de personal. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete-pf" onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
